/**
 * SummaryProducts.jsx
 * Exact replica of Excel "Summary_Products" sheet
 * All 44 columns, exact color coding matching Excel:
 *   🟩 #CCFFCC (GREEN)      = auto-calculated from forms (read-only)
 *   🟨 #FFFF00 (YELLOW)     = required user input
 *   🟡 #FFFFCC (LT_YELLOW)  = optional / conditional user input
 *
 * CN CODE FEATURE (added):
 *   - Col 3 (CN Codes) now uses <CnCodeAutocomplete /> instead of plain input
 *   - Options filtered by Col 2 (aggregated good) for each row
 *   - Selecting a code auto-fills Col 4 (CN Name) in the same row
 *   - Manual cell state lives in ProductManualContext (shared with
 *     SummaryCommunication) so values stay in sync between the two pages
 */

import React, { useMemo, useState, useCallback } from "react";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useFormContext } from "../context/FormContext";
import { useProductManual } from "../context/ProductManualContext";
import { D as DFormulas } from "../utils/CbamFormulas";
import exportSummaryExcel from "../utils/exportSummaryExcel";
import { buildPrecursorData } from "../utils/exportSummaryExcel";
import { getDescriptionForCode, getDescriptionForCompactCode, getDisplayCodeForCompact } from "../utils/cnCodeLookup";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const n6 = (v, d = 6) => {
  const x = Number(v);
  return Number.isFinite(x) && x !== 0
    ? x.toLocaleString("en-US", { maximumFractionDigits: d })
    : "";
};
const fmtDate = (v) => {
  if (!v) return "";
  try {
    return new Date(v).toLocaleDateString("en-GB");
  } catch {
    return String(v);
  }
};

// Compute default-value EmbEm direct contributed by an upstream process when
// `qty` tonnes of it are consumed downstream.
const computeDefaultEmFromUpstream = (upstreamProc, formEPrecursors, qty) => {
  if (!upstreamProc || !qty) return 0;
  const upstreamId = upstreamProc.processId || upstreamProc.id;
  const upstreamName = upstreamProc.title || upstreamProc.name || "";
  const matches = (cp) => {
    if (!cp.processId && !cp.process) return false;
    return (
      cp.processId === upstreamId ||
      cp.processId === upstreamProc.id ||
      cp.processId === upstreamProc.processId ||
      cp.process === upstreamName ||
      cp.processId === upstreamName
    );
  };
  const upstreamProd = (upstreamProc.productionLevels || []).reduce(
    (s, r) => s + Number(r.amounts || 0),
    0,
  );
  if (upstreamProd <= 0) return 0;
  return formEPrecursors.reduce((sum, prec) => {
    if (prec.emissions?.sourceSeeDirect !== "Default") return sum;
    const consumed = (prec.consumedInProcesses || []).find(matches);
    if (!consumed || !Number(consumed.amounts)) return sum;
    const seeDirect = Number(prec.emissions?.seeDirect || 0);
    const fractionInUpstream = Number(consumed.amounts) / upstreamProd;
    return sum + seeDirect * fractionInUpstream * qty;
  }, 0);
};

// ─── 44 Column Definitions ───────────────────────────────────────────────────
const COLS = [
  {
    n: 1,
    excel: "D",
    label: "Production process from which the products arise",
    color: "yellow",
    w: 120,
    src: "FormD",
  },
  {
    n: 2,
    excel: "E",
    label: "Type of aggregated good or precursor",
    color: "green",
    w: 155,
    src: "FormD",
  },
  { n: 3, excel: "F", label: "CN Codes", color: "green", w: 160, src: "FormA" },
  { n: 4, excel: "G", label: "CN Name", color: "green", w: 220, src: "auto" },
  {
    n: 5,
    excel: "H",
    label:
      "Product name\n(used for communication with reporting declarant, e.g. on invoices)",
    color: "yellow",
    w: 160,
    src: "manual",
  },
  {
    n: 6,
    excel: "I",
    label: "SEE (direct)\ntCO2e/t",
    color: "green",
    w: 90,
    src: "FormD",
  },
  {
    n: 7,
    excel: "J",
    label: "SEE (indirect)\ntCO2e/t",
    color: "green",
    w: 90,
    src: "FormD",
  },
  {
    n: 8,
    excel: "K",
    label: "SEE (total)\ntCO2e/t",
    color: "green",
    w: 90,
    src: "FormD",
  },
  { n: 9, excel: "L", label: "Unit", color: "green", w: 60, src: "FormD" },
  {
    n: 10,
    excel: "M",
    label: "Share of emissions by default value",
    color: "green",
    w: 115,
    src: "FormE",
  },
  {
    n: 11,
    excel: "N",
    label: "Source for electricity EF",
    color: "green",
    w: 115,
    src: "FormD",
  },
  {
    n: 12,
    excel: "O",
    label: "Embedded electricity (MWh/t)",
    color: "green",
    w: 105,
    src: "FormD",
  },
  {
    n: 13,
    excel: "P",
    label: "The main reducing agent of the precursor, if known",
    color: "yellow",
    w: 140,
    src: "manual",
  },
  {
    n: 14,
    excel: "Q",
    label: "Steel mill identification number",
    color: "yellow",
    w: 115,
    src: "manual",
  },
  {
    n: 15,
    excel: "R",
    label: "% Mn",
    color: "lt_yellow",
    w: 55,
    src: "manual",
  },
  {
    n: 16,
    excel: "S",
    label: "% Cr",
    color: "lt_yellow",
    w: 55,
    src: "manual",
  },
  {
    n: 17,
    excel: "T",
    label: "% Ni",
    color: "lt_yellow",
    w: 55,
    src: "manual",
  },
  {
    n: 18,
    excel: "U",
    label: "% other alloys",
    color: "lt_yellow",
    w: 80,
    src: "manual",
  },
  {
    n: 19,
    excel: "V",
    label: "% carbon",
    color: "lt_yellow",
    w: 65,
    src: "manual",
  },
  {
    n: 20,
    excel: "W",
    label: "t scrap per t steel",
    color: "lt_yellow",
    w: 90,
    src: "manual",
  },
  {
    n: 21,
    excel: "X",
    label: "% other materials",
    color: "lt_yellow",
    w: 85,
    src: "manual",
  },
  {
    n: 22,
    excel: "Y",
    label: "% pre-consumer scrap",
    color: "lt_yellow",
    w: 90,
    src: "manual",
  },
  {
    n: 23,
    excel: "Z",
    label: "t scrap per t aluminium",
    color: "lt_yellow",
    w: 95,
    src: "manual",
  },
  {
    n: 24,
    excel: "AA",
    label: "% non-aluminium elements",
    color: "lt_yellow",
    w: 95,
    src: "manual",
  },
  {
    n: 25,
    excel: "AB",
    label: "Clinker factor",
    color: "yellow",
    w: 80,
    src: "manual",
  },
  {
    n: 26,
    excel: "AC",
    label: "Calcined or not",
    color: "yellow",
    w: 80,
    src: "manual",
  },
  {
    n: 27,
    excel: "AD",
    label: "Concentration, if hydrous solution",
    color: "yellow",
    w: 110,
    src: "manual",
  },
  {
    n: 28,
    excel: "AE",
    label: "% nitric acid",
    color: "yellow",
    w: 75,
    src: "manual",
  },
  {
    n: 29,
    excel: "AF",
    label: "% urea",
    color: "yellow",
    w: 65,
    src: "manual",
  },
  {
    n: 30,
    excel: "AG",
    label: "% N contained",
    color: "yellow",
    w: 80,
    src: "manual",
  },
  {
    n: 31,
    excel: "AH",
    label: "% N as ammonium (NH4+)",
    color: "yellow",
    w: 108,
    src: "manual",
  },
  {
    n: 32,
    excel: "AI",
    label: "% N as nitrate (NO3–)",
    color: "yellow",
    w: 103,
    src: "manual",
  },
  {
    n: 33,
    excel: "AJ",
    label: "% N as Urea",
    color: "yellow",
    w: 75,
    src: "manual",
  },
  {
    n: 34,
    excel: "AK",
    label: "% N in other (organic) forms",
    color: "yellow",
    w: 108,
    src: "manual",
  },
  {
    n: 35,
    excel: "AL",
    label: "Type of instrument (carbon pricing)",
    color: "lt_yellow",
    w: 128,
    src: "FormA/F",
  },
  {
    n: 36,
    excel: "AM",
    label: "Share of total embedded emissions covered by the carbon price",
    color: "lt_yellow",
    w: 148,
    src: "FormF",
  },
  {
    n: 37,
    excel: "AN",
    label: "Embedded emissions covered by the carbon price",
    color: "green",
    w: 138,
    src: "FormF",
  },
  {
    n: 38,
    excel: "AP",
    label: "Currency",
    color: "lt_yellow",
    w: 68,
    src: "FormF",
  },
  {
    n: 39,
    excel: "AR",
    label: "Carbon price (CP) due\n(per produced t or MWh)",
    color: "lt_yellow",
    w: 108,
    src: "FormF",
  },
  {
    n: 40,
    excel: "AT",
    label: "Type of rebate",
    color: "lt_yellow",
    w: 90,
    src: "manual",
  },
  {
    n: 41,
    excel: "AU",
    label: "Share of embedded emissions covered by the rebate",
    color: "lt_yellow",
    w: 143,
    src: "manual",
  },
  {
    n: 42,
    excel: "AV",
    label: "Embedded emissions covered by rebate",
    color: "green",
    w: 128,
    src: "FormF",
  },
  {
    n: 43,
    excel: "AX",
    label: "Amount of rebate\n(per produced t or MWh)",
    color: "lt_yellow",
    w: 108,
    src: "FormF",
  },
  {
    n: 44,
    excel: "AZ",
    label: "Result: Effective CP due\n(per produced t or MWh)",
    color: "green",
    w: 128,
    src: "FormF",
  },
];

const COL_GROUPS = [
  { label: "Product Identification", cols: [1, 5], color: "#254244" },
  {
    label: "Specific Embedded Emissions (SEE) & Electricity",
    cols: [6, 12],
    color: "#1a5c41",
  },
  { label: "Additional Product Parameters", cols: [13, 34], color: "#5a3800" },
  { label: "Carbon Pricing", cols: [35, 44], color: "#2a3c7a" },
];

// ─── CSS ──────────────────────────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;600;700&display=swap');

  .sp { font-family: 'IBM Plex Sans', Calibri, Arial, sans-serif; font-size: 11px; color: #111; background: #f0f2f4; min-height: 100vh; padding-bottom: 40px; }
  .sp-top { background: #1d3c3e; color: #e8f0f1; padding: 10px 16px; display: flex; align-items: center; gap: 12px; flex-wrap: wrap; box-shadow: 0 2px 8px rgba(0,0,0,.25); }
  .sp-top-title { font-size: 14px; font-weight: 600; flex: 1; letter-spacing: 0.2px; }
  .sp-top-sub   { font-size: 10px; opacity: .65; }
  .sp-top-badge { background: rgba(255,255,255,.12); border: 1px solid rgba(255,255,255,.25); padding: 2px 10px; font-size: 10px; border-radius: 3px; }
  .sp-info { background: #fff8e6; border-bottom: 2px solid #f5c518; padding: 6px 16px; font-size: 10.5px; color: #5c4a00; display: flex; align-items: center; gap: 8px; }
  .sp-info-icon { font-size: 13px; }
  .sp-toolbar { background: #fff; border-bottom: 1px solid #d8dde3; padding: 6px 14px; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  .sp-btn { padding: 4px 12px; border-radius: 3px; font-size: 10.5px; cursor: pointer; font-family: inherit; border: 1px solid #c0c7ce; background: #f5f7f8; color: #333; transition: background .15s; }
  .sp-btn:hover { background: #e8edf0; }
  .sp-btn.primary { background: #376366; color: #fff; border-color: #376366; }
  .sp-btn.primary:hover { background: #2a4d50; }
  .sp-filter-wrap { display: flex; align-items: center; gap: 6px; font-size: 10.5px; }
  .sp-filter-wrap select { padding: 3px 6px; font-size: 10.5px; font-family: inherit; border: 1px solid #c0c7ce; border-radius: 3px; }
  .sp-legend { background: #fff; border-bottom: 1px solid #d8dde3; padding: 5px 14px; display: flex; align-items: center; gap: 20px; flex-wrap: wrap; font-size: 10px; color: #555; }
  .sp-legend-item { display: flex; align-items: center; gap: 5px; }
  .sp-swatch { width: 14px; height: 14px; border: 1px solid rgba(0,0,0,.18); border-radius: 2px; flex-shrink: 0; }
  .sp-scroll { overflow-x: auto; }
  .sp-tbl { border-collapse: collapse; font-size: 10.5px; white-space: nowrap; }
  .sp-tbl th, .sp-tbl td { border: 1px solid #c5cdd4; padding: 0; vertical-align: middle; }
  .h-group { font-weight: 700; font-size: 10px; text-align: center; padding: 4px 6px; color: #fff; letter-spacing: 0.3px; }
  .h-col { font-weight: 600; font-size: 9.5px; text-align: center; padding: 4px 4px 3px; line-height: 1.3; vertical-align: bottom; white-space: pre-wrap; word-break: break-word; color: #fff; background: #2c5456; }
  .h-col-n { font-size: 8px; opacity: .65; display: block; margin-bottom: 2px; }
  .h-col-src { font-size: 7.5px; opacity: .6; display: block; margin-top: 2px; font-weight: 400; }
  .d-cell { padding: 2px 5px; font-size: 10.5px; height: 22px; min-height: 22px; }
  .d-green    { background: #CCFFCC; }
  .d-yellow   { background: #FFFF00; }
  .d-ltyellow { background: #FFFFCC; }
  .d-id       { background: #E5F0F1; color: #1d3c3e; font-weight: 600; text-align: center; }
  .d-num      { text-align: right; font-variant-numeric: tabular-nums; }
  .d-center   { text-align: center; }
  .sp-input { width: 100%; height: 100%; border: none; background: transparent; font-size: 10.5px; font-family: inherit; padding: 2px 5px; outline: none; color: #000; box-sizing: border-box; }
  .sp-input:focus { background: rgba(0,100,255,.06); }
  /* CN code autocomplete cell — let MUI take over but keep background yellow */
  .d-cn-cell { padding: 0 2px; }
  .d-cn-cell .MuiAutocomplete-root { background: transparent; }
  .d-cn-cell .MuiInputBase-root { padding: 0 !important; min-height: 22px; }
  .d-cn-cell input { font-size: 10.5px !important; }
  /* CN name read-only cell — wrap long descriptions */
  .d-cnname { white-space: normal; word-break: break-word; font-size: 10px; line-height: 1.25; padding: 3px 5px; max-width: 260px; }
  .sp-tbl tbody tr:nth-child(even) .d-green    { background: #b8f0b8; }
  .sp-tbl tbody tr:nth-child(even) .d-yellow   { background: #eded00; }
  .sp-tbl tbody tr:nth-child(even) .d-ltyellow { background: #f5f5c0; }
  .sp-tbl tbody tr:nth-child(even) .d-id       { background: #d8eaeb; }
  .sp-tbl tr.ex-row td { opacity: .55; font-style: italic; }
  .sp-tbl tr.sec-divider td { background: #376366; color: #fff; font-weight: 700; font-size: 10px; padding: 3px 8px; }
  .sp-empty { text-align: center; padding: 30px; color: #888; font-size: 12px; background: #fff; }
  .sp-empty-icon { font-size: 32px; display: block; margin-bottom: 8px; }
  .sp-count { background: #376366; color: #fff; padding: 1px 7px; border-radius: 10px; font-size: 10px; font-weight: 600; }
  .sp-tbl tbody tr:hover td { filter: brightness(0.96); }
`;

// ─── Main Component ───────────────────────────────────────────────────────────
const SummaryProducts = ({ readOnly = false }) => {
  const { allFormData } = useFormContext();

  //  NEW: shared manual cell state (replaces local useState)
  const { getRow, setCell, setCells, data: manualData } = useProductManual();

  const fA = allFormData?.formA || {};
  const fD = allFormData?.formD || {};
  const fE = allFormData?.formE || {};
  const fF = allFormData?.formF || {};

  // Variants — kept local because they're UI-only and per-page
  const [variants, setVariants] = useState({});
  const addVariant = useCallback((procId) => {
    setVariants((prev) => ({
      ...prev,
      [procId]: [...(prev[procId] || []), `v-${Date.now()}`],
    }));
  }, []);
  const removeVariant = useCallback((procId, variantId) => {
    setVariants((prev) => ({
      ...prev,
      [procId]: (prev[procId] || []).filter((id) => id !== variantId),
    }));
  }, []);

  const [filterGroup, setFilterGroup] = useState("all");
  const [showExample, setShowExample] = useState(true);

  const formAProcs = fA.relevantProcesses || [];
  const aggregatedGoods = fA.aggregatedGoods || [];
  const purchasedPrecursors = fA.purchasedPrecursors || [];

  const goodsLookup = Object.fromEntries(
    aggregatedGoods.map((g) => [g.category, g]),
  );

  // CN code now comes from the precursor linked to this process.
  // Prefer explicit Form A links, but fall back to Form E precursors
  // auto-matched by goods category / process if Form A row has no link.
  const getCnCodeForProcess = (processId) => {
    const linkedA = purchasedPrecursors.find(
      (pp) => pp.linkedProcessId === processId,
    );
    if (linkedA?.cnCode) return linkedA.cnCode;

    const linkedE = formEPrecursors.find(
      (ep) => ep.linkedProcessId === processId,
    );
    return linkedE?.emissions?.cnCode || "";
  };

  const dProcesses = fD?.processes || [];
  const formEPrecursors = fE?.precursors || [];

  const makeMatchesProc = (proc) => {
    const procId = proc.processId || proc.id;
    const procName = proc.title || proc.name || "";
    return (cp) => {
      if (!cp.processId && !cp.process) return false;
      return (
        cp.processId === procId ||
        cp.processId === proc.id ||
        cp.processId === proc.processId ||
        cp.process === procName ||
        cp.processId === procName
      );
    };
  };

  const processRows = useMemo(
    () =>
      dProcesses.map((proc, idx) => {
        const aProc = formAProcs[idx] || {};
        const id = proc.id || proc.processId || aProc.id || `P${idx + 1}`;
        const name = proc.name || proc.title || aProc.name || `P${idx + 1}`;
        const cat =
          aProc.aggregatedGoodsCategory || proc.aggregatedGoodsCategory || "";
        const cnCodeCompact = getCnCodeForProcess(id);
        const cnCode = getDisplayCodeForCompact(cnCodeCompact);
        const cnName = getDescriptionForCompactCode(cnCodeCompact);

        const see = DFormulas.calculateSEE(
          { ...proc, id, name, aggregatedGoodsCategory: cat },
          formEPrecursors,
        );
        const totProd = see.productionLevelsTotalAuto || 0;
        const elecMWh = Number(proc.electricityConsumption?.consumption || 0);
        const procElecPerT = totProd > 0 ? elecMWh / totProd : 0;

        const matchesProc = makeMatchesProc(proc);
        const directPrecElecSum = formEPrecursors.reduce((sum, prec) => {
          const consumed = (prec.consumedInProcesses || []).find(matchesProc);
          if (!consumed || !Number(consumed.amounts)) return sum;
          const massPerT = totProd > 0 ? Number(consumed.amounts) / totProd : 0;
          return sum + massPerT * Number(prec.emissions?.secIndirect || 0);
        }, 0);

        const procIdx = dProcesses.findIndex(
          (p) => (p.processId || p.id) === (proc.processId || proc.id),
        );
        const consumedProcSum = (proc.consumedOtherProcesses || []).reduce(
          (acc, cp2) => {
            if (!cp2.processId) return acc;
            if (cp2.processId === (proc.processId || proc.id)) return acc;
            const otherProc = dProcesses.find(
              (p) => (p.processId || p.id) === cp2.processId,
            );
            if (!otherProc) return acc;
            const otherIdx = dProcesses.findIndex(
              (p) => (p.processId || p.id) === cp2.processId,
            );
            if (procIdx <= otherIdx) return acc;
            const otherSee = DFormulas.calculateSEE(otherProc, formEPrecursors);
            const otherProd = otherSee.productionLevelsTotalAuto || 0;
            const explicitQty = Number(cp2.amounts);
            const derivedQty =
              explicitQty > 0
                ? explicitQty
                : otherProd -
                  Number(
                    otherProc.productionDetails?.amountsProducedForMarket || 0,
                  );
            const qty = derivedQty > 0 ? derivedQty : 0;
            if (!qty) return acc;
            const massPerT = totProd > 0 ? qty / totProd : 0;
            const otherElec = Number(
              otherProc.electricityConsumption?.consumption || 0,
            );
            const otherProcElecT = otherProd > 0 ? otherElec / otherProd : 0;
            const otherMatchesProc = makeMatchesProc(otherProc);
            const otherPrecElecT = formEPrecursors.reduce((s, prec) => {
              const cInOther = (prec.consumedInProcesses || []).find(
                otherMatchesProc,
              );
              if (!cInOther || !Number(cInOther.amounts)) return s;
              const m =
                otherProd > 0 ? Number(cInOther.amounts) / otherProd : 0;
              return s + m * Number(prec.emissions?.secIndirect || 0);
            }, 0);
            const otherFullElecT = otherProcElecT + otherPrecElecT;
            return {
              direct: acc.direct + (otherSee.see_direct || 0) * massPerT,
              indirect: acc.indirect + (otherSee.see_indirect || 0) * massPerT,
              elec: acc.elec + otherFullElecT * massPerT,
              defaultEm:
                acc.defaultEm +
                computeDefaultEmFromUpstream(otherProc, formEPrecursors, qty),
            };
          },
          { direct: 0, indirect: 0, elec: 0, defaultEm: 0 },
        );

        const fullSeeDirect = (see.see_direct || 0) + consumedProcSum.direct;
        const fullSeeIndirect =
          (see.see_indirect || 0) + consumedProcSum.indirect;
        const fullSeeTotal = fullSeeDirect + fullSeeIndirect;
        const embElecPerT =
          procElecPerT + directPrecElecSum + consumedProcSum.elec;

        const directDefaultEm = formEPrecursors.reduce((sum, prec) => {
          if (prec.emissions?.sourceSeeDirect !== "Default") return sum;
          const consumed = (prec.consumedInProcesses || []).find(matchesProc);
          if (!consumed || !Number(consumed.amounts)) return sum;
          return (
            sum +
            Number(prec.emissions?.seeDirect || 0) * Number(consumed.amounts)
          );
        }, 0);
        const totalDefaultEm = directDefaultEm + consumedProcSum.defaultEm;
        const totalEmbDirect = fullSeeDirect * totProd;
        const defaultShare =
          totalEmbDirect > 0 ? totalDefaultEm / totalEmbDirect : 0;

        const cpRow =
          (fF?.carbonRows || []).find(
            (r) => r.processId === (proc.processId || proc.id),
          ) || {};
        const shareF = Number(cpRow.share_of_emissions_covered || 0);
        const embCovered = n6(shareF * fullSeeTotal);
        const rebateF = Number(cpRow.rebate_per_produced_t_or_MWh || 0);

        return {
          rowKey: `proc-${id}`,
          isPrecursor: false,
          col1: name,
          col1Id: id,
          col2: cat,
          cnCode,
          cnName,
          col6: n6(fullSeeDirect),
          col7: n6(fullSeeIndirect),
          col8: n6(fullSeeTotal),
          col9: "tCO2e/t",
          col10: defaultShare > 0 ? (defaultShare * 100).toFixed(2) + "%" : "0",
          col11: proc.electricityConsumption?.source || "",
          col12: n6(embElecPerT),
          col35: fA.carbonPriceInstrument || "",
          col36: n6(shareF),
          col37: embCovered,
          col38: fF?.currency || "EUR",
          col39: n6(Number(cpRow.cp_due_per_produced_t_or_MWh || 0)),
          col42: n6(rebateF),
          col43: n6(rebateF),
          col44: n6(Number(cpRow.result_effective_cp || 0)),
        };
      }),
    [dProcesses, formAProcs, fF, fA, formEPrecursors], // eslint-disable-line
  );

  const precRows = useMemo(() => {
    return buildPrecursorData(allFormData).map((prec) => ({
      rowKey: `prec-${prec.id}`,
      isPrecursor: true,
      col1: prec.id,
      col1Id: prec.id,
      col2: prec.goodsType || prec.name || "",
      cnCode: prec.cnCode,
      cnName: prec.cnName,
      col6: n6(prec.seeDirect),
      col7: n6(prec.seeIndirect),
      col8: n6(prec.seeTotal),
      col9: "tCO2e/t",
      col10: prec.isDefault ? "TRUE" : "FALSE",
      col11: prec.elecSrc || "",
      col12: n6(prec.elecPerT),
      col35: prec.cpInstrument || "",
      col36: "",
      col37: "",
      col38: prec.currency || "EUR",
      col39: n6(prec.cpDue),
      col42: n6(prec.rebate),
      col43: n6(prec.rebate),
      col44: n6(prec.effectiveCp),
    }));
  }, [allFormData]);

  const EXAMPLE_ROW = {
    rowKey: "ex-row",
    isPrecursor: false,
    isExample: true,
    col1: "Example process A",
    col2: "Iron or steel products",
    cnCode: "7207 19 19",
    cnName: getDescriptionForCode("7207 19 19"),
    col6: "0.915",
    col7: "0.396",
    col8: "1.311",
    col9: "tCO2e/t",
    col10: "0",
    col11: "D.4.1",
    col12: "0.281",
    col35: "Carbon Tax",
    col36: "0.698",
    col37: "0.915",
    col38: "USD",
    col39: "50",
    col42: "1.180",
    col43: "20",
    col44: "35.413",
  };

  const EXAMPLE_MANUAL = {
    5: "Example name A",
    13: "Coal or coke",
    14: "623108",
    15: "0.1395",
    16: "0.0841",
    17: "0.021",
    18: "0.030",
    19: "0.0035",
    20: "",
    21: "0.0001",
    40: "Tax deduction",
    41: "0.9",
  };

  const visibleCols = useMemo(() => {
    if (filterGroup === "all") return COLS;
    const map = {
      product: [1, 5],
      see: [6, 12],
      params: [13, 34],
      carbon: [35, 44],
    };
    const [lo, hi] = map[filterGroup] || [1, 44];
    return COLS.filter((c) => c.n >= lo && c.n <= hi);
  }, [filterGroup]);

  const renderCell = (row, col) => {
    const m = row.isExample
      ? { ...getRow(row.rowKey), ...EXAMPLE_MANUAL }
      : getRow(row.rowKey);

    const manInput = (placeholder = "") => (
      <td
        key={col.n}
        className={`d-cell d-${col.color === "lt_yellow" ? "ltyellow" : col.color}`}
        style={{ minWidth: col.w, maxWidth: col.w + 40 }}
      >
        <input
          className="sp-input"
          value={m[col.n] ?? ""}
          onChange={(e) =>
            !readOnly && setCell(row.rowKey, col.n, e.target.value)
          }
          placeholder={placeholder}
          readOnly={readOnly}
          disabled={readOnly}
          style={readOnly ? { cursor: "default", opacity: 0.85 } : undefined}
        />
      </td>
    );

    const autoCell = (val, extra = "") => (
      <td
        key={col.n}
        className={`d-cell d-green${extra}`}
        style={{ minWidth: col.w, maxWidth: col.w + 40 }}
      >
        {val || ""}
      </td>
    );

    switch (col.n) {
      case 1:
        return (
          <td
            key={col.n}
            className="d-cell d-id"
            style={{ minWidth: col.w, maxWidth: col.w + 40 }}
          >
            {row.isVariant ? (
              <span style={{ paddingLeft: 14, color: "#5a7a7c" }}>
                <span
                  style={{
                    fontSize: 9,
                    color: "#82a0a3",
                    fontWeight: 700,
                    marginRight: 4,
                  }}
                >
                  ↳ #{row.variantNum}
                </span>
                {row.col1}
              </span>
            ) : (
              <>
                {row.col1Id && (
                  <span
                    style={{
                      fontSize: 9,
                      color: "#376366",
                      fontWeight: 700,
                      marginRight: 4,
                      fontFamily: "monospace",
                    }}
                  >
                    {row.col1Id}
                  </span>
                )}
                {row.col1}
              </>
            )}
          </td>
        );
      case 2:
        return autoCell(row.col2);
      //  Col 3 (CN Codes) — read-only, comes from Form A's aggregated goods table only
      case 3:
        return (
          <td
            key={col.n}
            className="d-cell d-green d-center"
            style={{ minWidth: col.w, maxWidth: col.w + 40 }}
          >
            {row.cnCode || (
              <span
                style={{ color: "#999", fontStyle: "italic", fontSize: 9.5 }}
              >
                Not set in Form A
              </span>
            )}
          </td>
        );
      //  Col 4 (CN Name) — read-only, derived from Form A's CN code
      case 4: {
        const cnName = row.cnName || "";
        return (
          <td
            key={col.n}
            className="d-cell d-green d-cnname"
            style={{ minWidth: col.w, maxWidth: col.w + 60 }}
            title={cnName}
          >
            {cnName || (
              <span
                style={{ color: "#999", fontStyle: "italic", fontSize: 9.5 }}
              >
                Set a CN code in Form A first
              </span>
            )}
          </td>
        );
      }
      case 5:
        return manInput("Product name for invoices");
      case 6:
        return autoCell(row.col6, " d-num");
      case 7:
        return autoCell(row.col7, " d-num");
      case 8:
        return autoCell(row.col8, " d-num");
      case 9:
        return autoCell(row.col9, " d-center");
      case 10:
        return autoCell(row.col10, " d-center");
      case 11:
        return autoCell(row.col11, " d-center");
      case 12:
        return autoCell(row.col12, " d-num");
      case 13:
        return manInput("e.g. Coal or coke");
      case 14:
        return manInput("Mill ID number");
      case 15:
      case 16:
      case 17:
      case 18:
      case 19:
      case 20:
      case 21:
      case 22:
      case 23:
      case 24:
      case 25:
      case 26:
      case 27:
      case 28:
      case 29:
      case 30:
      case 31:
      case 32:
      case 33:
      case 34:
        return manInput();
      case 35:
        return (
          <td
            key={col.n}
            className="d-cell d-ltyellow"
            style={{ minWidth: col.w }}
          >
            {row.col35}
          </td>
        );
      case 36:
        return (
          <td
            key={col.n}
            className="d-cell d-ltyellow d-num"
            style={{ minWidth: col.w }}
          >
            {row.col36}
          </td>
        );
      case 37:
        return autoCell(row.col37, " d-num");
      case 38:
        return (
          <td
            key={col.n}
            className="d-cell d-ltyellow d-center"
            style={{ minWidth: col.w }}
          >
            {row.col38}
          </td>
        );
      case 39:
        return (
          <td
            key={col.n}
            className="d-cell d-ltyellow d-num"
            style={{ minWidth: col.w }}
          >
            {row.col39}
          </td>
        );
      case 40:
        return manInput("e.g. Tax deduction");
      case 41:
        return manInput();
      case 42:
        return autoCell(row.col42, " d-num");
      case 43:
        return (
          <td
            key={col.n}
            className="d-cell d-ltyellow d-num"
            style={{ minWidth: col.w }}
          >
            {row.col43}
          </td>
        );
      case 44:
        return autoCell(row.col44, " d-num");
      default:
        return <td key={col.n} style={{ minWidth: col.w }} />;
    }
  };

  const expandedRows = useMemo(() => {
    const out = [];
    processRows.forEach((base) => {
      out.push({ ...base, isVariant: false });
      const procVariants = variants[base.col1Id] || [];
      procVariants.forEach((variantId, vi) => {
        out.push({
          ...base,
          rowKey: `${base.rowKey}__${variantId}`,
          isVariant: true,
          variantId,
          parentProcId: base.col1Id,
          variantNum: vi + 2,
        });
      });
    });
    return out;
  }, [processRows, variants]);

  const allRows = [...expandedRows, ...precRows];
  const totalRows = allRows.length;

  return (
    <>
      <style>{CSS}</style>
      <div className="sp">
        <div className="sp-top">
          <div>
            <div className="sp-top-title">Summary of Products</div>
            <div className="sp-top-sub">
              Reporting period: {fmtDate(fA.reportingPeriodStart)} –{" "}
              {fmtDate(fA.reportingPeriodEnd)} &nbsp;|&nbsp;{" "}
              {fA.installationNameEnglish ||
                fA.installationName ||
                "Installation not set"}
            </div>
          </div>
          <span className="sp-top-badge">Summary_Products</span>
          <span className="sp-top-badge">44 columns</span>
          <span className="sp-count">
            {totalRows} row{totalRows !== 1 ? "s" : ""}
          </span>
          <span
            className="sp-top-badge"
            onClick={() => exportSummaryExcel(allFormData, manualData)}
            style={{
              cursor: "pointer",
              backgroundColor: "#C6EFCE",
              color: "#1a5e30",
              fontWeight: 700,
              border: "1px solid #82c891",
            }}
          >
            ⬇ Export to Excel
          </span>
        </div>

        <div className="sp-info">
          <span className="sp-info-icon">ℹ</span>
          CN code and CN name are set on Form A's Purchased Precursors tab and
          shown here read-only.
        </div>

        <div className="sp-toolbar">
          <span style={{ fontWeight: 600, fontSize: 10.5 }}>View columns:</span>
          <div className="sp-filter-wrap">
            <select
              value={filterGroup}
              onChange={(e) => setFilterGroup(e.target.value)}
            >
              <option value="all">All 44 columns</option>
              <option value="product">Product identification (1–5)</option>
              <option value="see">SEE & Electricity (6–12)</option>
              <option value="params">Additional parameters (13–34)</option>
              <option value="carbon">Carbon pricing (35–44)</option>
            </select>
          </div>
          <button className="sp-btn" onClick={() => setShowExample((v) => !v)}>
            {showExample ? "Hide" : "Show"} example row
          </button>
        </div>

        <div className="sp-legend">
          <div className="sp-legend-item">
            <div className="sp-swatch" style={{ background: "#CCFFCC" }} />
            <span>Auto-filled (read-only)</span>
          </div>
          <div className="sp-legend-item">
            <div className="sp-swatch" style={{ background: "#FFFF00" }} />
            <span>Required manual entry</span>
          </div>
          <div className="sp-legend-item">
            <div className="sp-swatch" style={{ background: "#FFFFCC" }} />
            <span>Optional / conditional entry</span>
          </div>
          <div className="sp-legend-item">
            <div className="sp-swatch" style={{ background: "#E5F0F1" }} />
            <span>ID (auto)</span>
          </div>
        </div>

        <div className="sp-scroll">
          <table className="sp-tbl">
            <thead>
              <tr>
                {COL_GROUPS.map((g) => {
                  const cols = visibleCols.filter(
                    (c) => c.n >= g.cols[0] && c.n <= g.cols[1],
                  );
                  if (!cols.length) return null;
                  return (
                    <th
                      key={g.label}
                      className="h-group"
                      colSpan={cols.length}
                      style={{
                        background: g.color,
                        minWidth: cols.reduce((s, c) => s + c.w, 0),
                      }}
                    >
                      {g.label}
                    </th>
                  );
                })}
              </tr>
              <tr>
                {visibleCols.map((col) => (
                  <th
                    key={col.n}
                    className="h-col"
                    style={{ minWidth: col.w, maxWidth: col.w + 40 }}
                  >
                    <span className="h-col-n">
                      [{col.n}] {col.excel}
                    </span>
                    {col.n === 5 ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "4px",
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        <span>{col.label}</span>

                        <Tooltip
                          arrow
                          placement="top"
                          title={
                            <>
                              Product name (e.g. article number) helps the
                              customer identify which emission data applies per
                              imported good, especially when the same CN code
                              comes from different production processes.
                              <br />
                              <br />
                              The customer's CN code must be used. Your own CN
                              code may differ from the customer's CN code, and
                              different customers may use different product
                              names.
                            </>
                          }
                        >
                          <InfoOutlinedIcon
                            sx={{
                              fontSize: 14,
                              cursor: "pointer",
                              color: "#ffffff",
                            }}
                          />
                        </Tooltip>
                      </div>
                    ) : (
                      col.label
                    )}
                    <span className="h-col-src">{col.src}</span>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {showExample && (
                <tr className="ex-row">
                  {visibleCols.map((col) => renderCell(EXAMPLE_ROW, col))}
                </tr>
              )}

              {expandedRows.length > 0 && (
                <tr className="sec-divider">
                  <td colSpan={visibleCols.length}>
                    Production processes ← Form D &nbsp;&nbsp;(
                    {processRows.length} process
                    {processRows.length !== 1 ? "es" : ""},{" "}
                    {expandedRows.length} product
                    {expandedRows.length !== 1 ? "s" : ""}) &nbsp;&nbsp;
                    <span
                      style={{ fontSize: 9, fontWeight: 400, opacity: 0.85 }}
                    >
                      Click "+ Add product variant" to add another CN-coded
                      product made by the same process
                    </span>
                  </td>
                </tr>
              )}
              {expandedRows.map((row, idx) => {
                const next = expandedRows[idx + 1];
                const isLastOfProcess = !next || next.col1Id !== row.col1Id;
                return (
                  <React.Fragment key={row.rowKey}>
                    <tr style={row.isVariant ? { background: "#fafdf9" } : {}}>
                      {visibleCols.map((col) => renderCell(row, col))}
                    </tr>
                    {isLastOfProcess && !readOnly && (
                      <tr>
                        <td
                          colSpan={visibleCols.length}
                          style={{
                            background: "#f6f9f8",
                            padding: "3px 8px",
                            border: "1px solid #c5cdd4",
                            fontSize: 10,
                          }}
                        >
                          <button
                            onClick={() => addVariant(row.col1Id)}
                            style={{
                              background: "#dbeed8",
                              border: "1px solid #82c891",
                              color: "#1a5e30",
                              fontSize: 10,
                              fontWeight: 600,
                              padding: "2px 8px",
                              borderRadius: 3,
                              cursor: "pointer",
                              fontFamily: "inherit",
                            }}
                          >
                            + Add product variant for {row.col1}
                          </button>
                          {row.isVariant && (
                            <button
                              onClick={() =>
                                removeVariant(row.parentProcId, row.variantId)
                              }
                              style={{
                                background: "transparent",
                                border: "1px solid #d99",
                                color: "#a33",
                                fontSize: 10,
                                padding: "2px 8px",
                                borderRadius: 3,
                                cursor: "pointer",
                                fontFamily: "inherit",
                                marginLeft: 8,
                              }}
                            >
                              Remove last variant
                            </button>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}

              {precRows.length > 0 && (
                <tr className="sec-divider">
                  <td colSpan={visibleCols.length}>
                    Purchased precursors ← Form A / E ({precRows.length} row
                    {precRows.length !== 1 ? "s" : ""})
                  </td>
                </tr>
              )}
              {precRows.map((row) => (
                <tr key={row.rowKey}>
                  {visibleCols.map((col) => renderCell(row, col))}
                </tr>
              ))}
              {allRows.length === 0 && (
                <tr>
                  <td colSpan={visibleCols.length} className="sp-empty">
                    <span className="sp-empty-icon">📋</span>
                    No data yet. Complete Forms A–F to populate this table.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default SummaryProducts;
